import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Play, Square, Clock, Zap } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Clockify-style Time Tracker with START/STOP
 * Prominent button that tracks time in real-time
 */
export const TimeTracker = ({ activeTask, startTracking, finishTracking, commesse, user }) => {
  const [isStartDialog, setIsStartDialog] = useState(false);
  const [description, setDescription] = useState('');
  const [commessa, setCommessa] = useState('');
  const [estimatedHours, setEstimatedHours] = useState('');
  const [elapsedTime, setElapsedTime] = useState('00:00:00');
  const [isLoading, setIsLoading] = useState(false);

  // Calculate elapsed time
  useEffect(() => {
    if (!activeTask) {
      setElapsedTime('00:00:00');
      return;
    }

    const updateElapsed = () => {
      const start = new Date(activeTask.start_time);
      const now = new Date();
      const diff = now - start;

      const hours = Math.floor(diff / 3600000);
      const minutes = Math.floor((diff % 3600000) / 60000);
      const seconds = Math.floor((diff % 60000) / 1000);

      setElapsedTime(
        `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
      );
    };

    updateElapsed();
    const interval = setInterval(updateElapsed, 1000);

    return () => clearInterval(interval);
  }, [activeTask]);

  const handleStart = async () => {
    if (!commessa || !description) {
      toast.error('Compila commessa e descrizione!');
      return;
    }

    setIsLoading(true);
    try {
      const selectedCommessa = commesse.find(c => c.code === commessa);

      await startTracking({
        commessa_id: selectedCommessa?.id,
        commessa_code: commessa,
        commessa_name: selectedCommessa?.name,
        description: description,
        estimated_hours: estimatedHours ? parseFloat(estimatedHours) : 1,
      });

      toast.success('⏱️ Timer avviato!', {
        description: `Tracking su ${selectedCommessa?.name || commessa}`,
      });

      // Reset form
      setDescription('');
      setCommessa('');
      setEstimatedHours('');
      setIsStartDialog(false);
    } catch (error) {
      console.error('Error starting tracker:', error);
      console.error('Error response:', error?.response?.data);

      let errorMsg = 'Errore sconosciuto';
      if (error?.response?.data?.detail) {
        if (typeof error.response.data.detail === 'string') {
          errorMsg = error.response.data.detail;
        } else if (Array.isArray(error.response.data.detail)) {
          errorMsg = error.response.data.detail.map(e => e.msg || JSON.stringify(e)).join(', ');
        } else {
          errorMsg = JSON.stringify(error.response.data.detail);
        }
      } else if (error?.message) {
        errorMsg = error.message;
      }

      toast.error('Errore avvio timer', {
        description: errorMsg
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleStop = async () => {
    if (!activeTask) return;

    setIsLoading(true);
    try {
      await finishTracking();
      toast.success('🎉 Tracking completato!', {
        description: 'Ore registrate con successo',
      });
    } catch (error) {
      console.error('Error stopping tracker:', error);
      console.error('Error response:', error?.response?.data);

      let errorMsg = 'Errore sconosciuto';
      if (error?.response?.data?.detail) {
        if (typeof error.response.data.detail === 'string') {
          errorMsg = error.response.data.detail;
        } else if (Array.isArray(error.response.data.detail)) {
          errorMsg = error.response.data.detail.map(e => e.msg || JSON.stringify(e)).join(', ');
        } else {
          errorMsg = JSON.stringify(error.response.data.detail);
        }
      } else if (error?.message) {
        errorMsg = error.message;
      }

      toast.error('Errore nel fermare il timer', {
        description: errorMsg
      });
    } finally {
      setIsLoading(false);
    }
  };

  // ACTIVE TIMER (tracking in progress)
  if (activeTask && activeTask.status === 'active') {
    return (
      <div className="fixed bottom-0 left-0 right-0 bg-gradient-to-r from-green-900 to-emerald-900 border-t-4 border-green-500 shadow-2xl z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-5">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            {/* Left: Task Info - Always visible */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-2.5 h-2.5 bg-green-400 rounded-full animate-pulse"></div>
                <span className="text-green-300 text-xs font-semibold uppercase tracking-wide">In corso</span>
              </div>
              <p className="text-white font-bold text-base sm:text-lg mb-1 truncate">
                {activeTask.description}
              </p>
              <p className="text-green-300 text-sm">
                {activeTask.commessa_name || activeTask.commessa_code}
              </p>
            </div>

            {/* Center: Elapsed Time */}
            <div className="flex items-center gap-4 sm:gap-6">
              <div className="text-center">
                <div className="flex items-center gap-2 mb-1">
                  <Clock className="w-5 h-5 text-green-400" />
                  <span className="text-4xl sm:text-5xl font-mono font-black text-white tracking-wider">
                    {elapsedTime}
                  </span>
                </div>
                <span className="text-green-400 text-xs">
                  Stima: {activeTask.estimated_hours || 1}h
                </span>
              </div>

              {/* Right: Stop Button */}
              <Button
                onClick={handleStop}
                disabled={isLoading}
                className="bg-red-600 hover:bg-red-500 text-white font-bold px-6 py-6 shadow-lg text-base whitespace-nowrap"
              >
                <Square className="w-5 h-5 mr-2 fill-current" />
                {isLoading ? 'Salvando...' : 'Ferma'}
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // START BUTTON (no tracking)
  return (
    <>
      <Button
        onClick={() => setIsStartDialog(true)}
        className="bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-500 hover:to-cyan-500 text-white font-bold"
        size="sm"
      >
        <Play className="w-4 h-4 mr-1" />
        <span className="hidden sm:inline">Avvia</span>
        <span className="sm:hidden">▶</span>
      </Button>

      {/* Start Dialog */}
      <Dialog open={isStartDialog} onOpenChange={setIsStartDialog}>
        <DialogContent className="bg-slate-900 border-blue-500/30 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-blue-300 flex items-center gap-2 text-xl">
              <Zap className="w-6 h-6" />
              Avvia Time Tracking
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 pt-4">
            {/* Commessa */}
            <div>
              <label className="text-sm text-slate-400 mb-2 block font-medium">
                Commessa *
              </label>
              <Select value={commessa} onValueChange={setCommessa}>
                <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
                  <SelectValue placeholder="Seleziona..." />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  {commesse?.map((c) => (
                    <SelectItem
                      key={c.id}
                      value={c.code}
                      className="text-white hover:bg-slate-700"
                    >
                      {c.code} - {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Description */}
            <div>
              <label className="text-sm text-slate-400 mb-2 block font-medium">
                Cosa stai facendo? *
              </label>
              <Input
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="es: Sviluppo feature login"
                className="bg-slate-800 border-slate-700 text-white"
                autoFocus
              />
            </div>

            {/* Estimated Hours */}
            <div>
              <label className="text-sm text-slate-400 mb-2 block font-medium">
                Ore stimate (opzionale)
              </label>
              <Input
                type="number"
                step="0.5"
                min="0"
                value={estimatedHours}
                onChange={(e) => setEstimatedHours(e.target.value)}
                placeholder="es: 2"
                className="bg-slate-800 border-slate-700 text-white"
              />
              <p className="text-xs text-slate-500 mt-1">
                Ti avviseremo quando il tempo stimato scade
              </p>
            </div>

            {/* Info Box */}
            <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-3">
              <p className="text-blue-300 text-xs">
                ⏱️ Il timer parte subito. Premi STOP quando finisci per registrare le ore automaticamente.
              </p>
            </div>

            {/* Actions */}
            <div className="flex gap-2 pt-2">
              <Button
                onClick={handleStart}
                disabled={isLoading || !commessa || !description}
                className="flex-1 bg-blue-600 hover:bg-blue-500 text-white font-bold h-12"
              >
                {isLoading ? (
                  <span className="animate-pulse">Avviando...</span>
                ) : (
                  <>
                    <Play className="w-5 h-5 mr-2" />
                    Avvia Timer
                  </>
                )}
              </Button>
              <Button
                onClick={() => setIsStartDialog(false)}
                variant="ghost"
                className="text-slate-400 hover:text-white"
              >
                Annulla
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default TimeTracker;
